import { Checkbox, Empty, Select, Spin, Switch } from "antd";
import { useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { USER_ENABLED_STATUS_ICON } from "src/assets/svgs";
import {
  useQueriesGetUserActivitiesBySystems,
  useQueryGetUserSystems,
} from "src/queries/users.queries";
import type {
  PermissionSelectValue,
  PermissionsBySystemId,
  UserActivityPermission,
  UserSystem,
} from "./types";
import {
  getResponseArray,
  getSystemName,
  isCrmSystem,
  normalizeUserActivityPermission,
  permissionToSelectValue,
  selectValueToPermission,
  shouldShowPermissionSettingsForRole,
  shouldShowSystemForRole,
} from "./userModalHelpers";

interface SystemPermissionsStepProps {
  userId?: string | number;
  roleId?: string | number;
  roleName?: string;
  permissionsBySystemId: PermissionsBySystemId;
  setPermissionsBySystemId: Dispatch<SetStateAction<PermissionsBySystemId>>;
  selectedSystemId?: string | number;
  setSelectedSystemId: (systemId?: string | number) => void;
  enabledSystemIds: Set<string | number>;
  setEnabledSystemIds: (ids: Set<string | number>) => void;
  setDirtyEnabledSystemIds: Dispatch<SetStateAction<Set<string>>>;
  setDirtyPermissionSystemIds: Dispatch<SetStateAction<Set<string>>>;
  setIsPermissionsLoading: (isLoading: boolean) => void;
  setSystems: (systems: UserSystem[]) => void;
}

const permissionOptions: Array<{ labelKey: string; value: PermissionSelectValue }> = [
  { labelKey: "enabled", value: "enabled" },
  { labelKey: "view", value: "canView" },
  { labelKey: "edit", value: "canEdit" },
  { labelKey: "delete", value: "canDelete" },
  { labelKey: "all", value: "all" },
];

const crmPermissionOptions: Array<{ labelKey: string; value: PermissionSelectValue }> = [
  ...permissionOptions,
  { labelKey: "UPSELLING", value: "upselling" },
];

const getSystemStateKey = (systemId?: string | number) =>
  systemId === undefined ? undefined : String(systemId);

const SystemPermissionsStep = ({
  userId,
  roleId,
  roleName,
  permissionsBySystemId,
  setPermissionsBySystemId,
  selectedSystemId,
  setSelectedSystemId,
  enabledSystemIds,
  setEnabledSystemIds,
  setDirtyEnabledSystemIds,
  setDirtyPermissionSystemIds,
  setIsPermissionsLoading,
  setSystems,
}: SystemPermissionsStepProps) => {
  const { t } = useTranslation();
  const initializedSystemsKeyRef = useRef<string | undefined>(undefined);
  const systemsQuery = useQueryGetUserSystems(userId, { enabled: Boolean(userId) });
  const systems = useMemo(
    () =>
      getResponseArray<UserSystem>(systemsQuery.data).filter((system) =>
        shouldShowSystemForRole(system, roleId, roleName)
      ),
    [roleId, roleName, systemsQuery.data]
  );
  const systemsWithPermissionSettings = useMemo(
    () =>
      systems.filter((system) => shouldShowPermissionSettingsForRole(system, roleId, roleName)),
    [roleId, roleName, systems]
  );
  const systemIdsWithPermissionSettings = useMemo(
    () => systemsWithPermissionSettings.map((system) => system.id),
    [systemsWithPermissionSettings]
  );
  const activityQueries = useQueriesGetUserActivitiesBySystems(
    userId,
    systemIdsWithPermissionSettings,
    {
      enabled: Boolean(userId && systemIdsWithPermissionSettings.length),
    }
  );
  const systemsStateKey = systems.map((system) => String(system.id)).join("|");
  const selectedSystemKey = getSystemStateKey(selectedSystemId);
  const selectedPermissions = selectedSystemKey
    ? (permissionsBySystemId[selectedSystemKey] ?? [])
    : [];
  const selectedSystemHasPermissionSettings =
    selectedSystemId !== undefined
      ? systemsWithPermissionSettings.some(
          (system) => String(system.id) === String(selectedSystemId)
        )
      : false;
  const selectedSystemIndex = systemsWithPermissionSettings.findIndex(
    (system) => String(system.id) === String(selectedSystemId)
  );
  const selectedActivitiesQuery =
    selectedSystemIndex >= 0 ? activityQueries[selectedSystemIndex] : undefined;
  const isSelectedActivitiesLoading = Boolean(
    selectedSystemKey &&
      permissionsBySystemId[selectedSystemKey] === undefined &&
      selectedActivitiesQuery?.isLoading
  );

  useEffect(() => {
    if (!systems.length || systemsQuery.isFetching) return;

    setSystems(systems);

    const nextInitializedKey = `${userId ?? "new"}:${systemsStateKey}`;
    if (initializedSystemsKeyRef.current !== nextInitializedKey) {
      setEnabledSystemIds(
        new Set(systems.filter((system) => system.isChecked).map((system) => system.id))
      );
      initializedSystemsKeyRef.current = nextInitializedKey;
    }

    const selectedSystemIsVisible = systems.some(
      (system) => String(system.id) === String(selectedSystemId)
    );

    if (!selectedSystemId || !selectedSystemIsVisible) {
      setSelectedSystemId(systems[0]?.id);
    }
  }, [
    selectedSystemId,
    setEnabledSystemIds,
    setSelectedSystemId,
    setSystems,
    systems,
    systemsQuery.isFetching,
    systemsStateKey,
    userId,
  ]);

  useEffect(() => {
    const isHydratingPermissions =
      systemsQuery.isLoading ||
      systemsQuery.isFetching ||
      systemsWithPermissionSettings.some((system, index) => {
        const systemKey = getSystemStateKey(system.id);
        const activityQuery = activityQueries[index];

        return (
          systemKey !== undefined &&
          permissionsBySystemId[systemKey] === undefined &&
          Boolean(activityQuery?.isLoading || activityQuery?.isFetching)
        );
      });

    setIsPermissionsLoading(isHydratingPermissions);
  }, [
    activityQueries,
    permissionsBySystemId,
    setIsPermissionsLoading,
    systemsWithPermissionSettings,
    systemsQuery.isFetching,
    systemsQuery.isLoading,
  ]);

  useEffect(() => {
    return () => setIsPermissionsLoading(false);
  }, [setIsPermissionsLoading]);

  useEffect(() => {
    if (!userId || !systemsWithPermissionSettings.length) return;

    systemsWithPermissionSettings.forEach((system, index) => {
      const systemKey = getSystemStateKey(system.id);
      const activityQuery = activityQueries[index];

      if (!systemKey) return;
      if (permissionsBySystemId[systemKey] !== undefined) return;
      if (!activityQuery?.data || activityQuery.isLoading || activityQuery.isFetching) return;

      const nextPermissions = getResponseArray<UserActivityPermission>(activityQuery.data);

      if (!nextPermissions.length) {
        setPermissionsBySystemId((previous) => ({
          ...previous,
          [systemKey]: [],
        }));
        return;
      }

      setPermissionsBySystemId((previous) => ({
        ...previous,
        [systemKey]: nextPermissions.map((permission) => ({
          ...normalizeUserActivityPermission(permission),
          userId,
        })),
      }));
    });
  }, [
    activityQueries,
    permissionsBySystemId,
    setPermissionsBySystemId,
    systemsWithPermissionSettings,
    userId,
  ]);

  const selectedSystem = systems.find((system) => String(system.id) === String(selectedSystemId));
  const selectedSystemIsCrm = isCrmSystem(selectedSystem);
  const selectedSystemEnabled =
    selectedSystemId !== undefined && enabledSystemIds.has(selectedSystemId);

  const handleToggleSystem = (system: UserSystem, checked: boolean) => {
    if (!userId) return;

    const nextEnabledIds = new Set(enabledSystemIds);

    if (checked) {
      nextEnabledIds.add(system.id);
    } else {
      nextEnabledIds.delete(system.id);
    }

    setEnabledSystemIds(nextEnabledIds);
    setDirtyEnabledSystemIds((previous) => new Set(previous).add(String(system.id)));
    setSelectedSystemId(system.id);
  };

  const translatedPermissionOptions = useMemo(
    () =>
      (selectedSystemIsCrm ? crmPermissionOptions : permissionOptions).map((option) => ({
        label: option.value === "upselling" ? option.labelKey : t(option.labelKey),
        value: option.value,
      })),
    [selectedSystemIsCrm, t]
  );

  const handlePermissionChange = (
    activityId: string | number,
    value: PermissionSelectValue,
    checked: boolean
  ) => {
    if (!selectedSystemKey) return;

    setDirtyPermissionSystemIds((previous) => new Set(previous).add(selectedSystemKey));
    setPermissionsBySystemId((previous) => ({
      ...previous,
      [selectedSystemKey]: (previous[selectedSystemKey] ?? []).map((permission) =>
        String(permission.activityId) === String(activityId)
          ? selectValueToPermission(permission, value, checked)
          : permission
      ),
    }));
  };

  return (
    <div className="user-modal-step">
      <h3 className="user-modal-section-title">{t("system_permissions")}</h3>
      <div className="user-permission-layout">
        <div className="user-permission-menu">
          {systemsQuery.isLoading ? (
            <Spin />
          ) : (
            systems.map((system) => {
              const enabled = enabledSystemIds.has(system.id);
              const active = String(system.id) === String(selectedSystemId);

              return (
                <button
                  type="button"
                  key={system.id}
                  className={`user-permission-menu-item ${active ? "active" : ""}`}
                  onClick={() => setSelectedSystemId(system.id)}
                >
                  <span>{getSystemName(system)}</span>
                  {enabled ? (
                    <img alt="" className="user-permission-status" src={USER_ENABLED_STATUS_ICON} />
                  ) : null}
                </button>
              );
            })
          )}
        </div>

        <div className="user-permission-panel">
          {selectedSystem ? (
            <>
              <div className="user-permission-panel-header">
                <div>
                  <h4>{getSystemName(selectedSystem)}</h4>
                  {selectedSystemEnabled && selectedSystemHasPermissionSettings ? (
                    <p>{t("permissions_panel_description")}</p>
                  ) : null}
                </div>
                <Switch
                  checked={selectedSystemEnabled}
                  onChange={(checked) => handleToggleSystem(selectedSystem, checked)}
                />
              </div>

              {selectedSystemEnabled && selectedSystemHasPermissionSettings ? (
                <div className="user-permission-list">
                  {isSelectedActivitiesLoading ? (
                    <div className="user-modal-loader">
                      <Spin />
                    </div>
                  ) : selectedPermissions.length ? (
                    selectedPermissions.map((permission) => {
                      const selectedValues = permissionToSelectValue(permission, {
                        showUpselling: selectedSystemIsCrm,
                      });

                      return (
                        <div className="user-permission-row" key={permission.activityId}>
                          <span>
                            {t(
                              permission.code ??
                              permission.activityCode ??
                                permission.activityName ??
                                String(permission.activityId)
                            )}
                          </span>
                          <Select
                            className="user-permission-select"
                            maxTagCount="responsive"
                            mode="multiple"
                            optionRender={(option) => (
                              <Checkbox
                                checked={selectedValues.includes(
                                  option.value as PermissionSelectValue
                                )}
                              >
                                {option.label}
                              </Checkbox>
                            )}
                            options={translatedPermissionOptions}
                            value={selectedValues}
                            onDeselect={(value) =>
                              handlePermissionChange(
                                permission.activityId,
                                value as PermissionSelectValue,
                                false
                              )
                            }
                            onSelect={(value) =>
                              handlePermissionChange(
                                permission.activityId,
                                value as PermissionSelectValue,
                                true
                              )
                            }
                          />
                        </div>
                      );
                    })
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemPermissionsStep;
